import { trigger } from '@angular/animations';
import { AsyncPipe, isPlatformServer, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  HostListener,
  inject,
  input,
  OnInit,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { map, of, switchMap, take } from 'rxjs';
import { moveAndFadeDownIf } from '../../../animations/move-and-fade-down.if.animation';
import { PICFLOW_ANALYTICS } from '../../../picflow-analytics';
import { PICFLOW_STORYBLOK } from '../../../picflow-storyblok';
import { BrowserService } from '../../../services/browser.service';
import { DialogService } from '../../../services/dialog.service';
import { StoryblokCdnPipe } from '../../../storyblok/pipes/storyblok-cdn.pipe';
import { StoryblokImage } from '../../../storyblok/storyblok-types';
import { ButtonDirective } from '../../directives/button.directive';
import { HeaderComponent } from '../header/header.component';
import { SvgComponent } from '../svg/svg.component';
import { MenuItem, WebsiteDesktopMenuComponent } from '../website-desktop-menu/website-desktop-menu.component';

@Component({
  selector: 'app-website-header',
  templateUrl: './website-header.component.html',
  styleUrls: ['./website-header.component.scss'],
  animations: [trigger('fixedDesktopHeader', moveAndFadeDownIf)],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    HeaderComponent,
    RouterLink,
    WebsiteDesktopMenuComponent,
    ButtonDirective,
    SvgComponent,
    NgTemplateOutlet,
    AsyncPipe,
    StoryblokCdnPipe,
  ],
})
export class WebsiteHeaderComponent implements OnInit {
  private dialog = inject(DialogService);
  private browserService = inject(BrowserService);
  private platformId = inject<string>(PLATFORM_ID);
  private route = inject(ActivatedRoute);
  private storyblokService = inject(PICFLOW_STORYBLOK);
  readonly color = input<'dark' | 'blue'>(undefined);
  readonly hideNav = input(false);
  readonly allowSticky = input(false);
  readonly customCta = input<'contact' | undefined>(undefined);
  scrolled = signal(false);
  headerOutOfView = signal(false);
  loggedIn = signal(false);
  windowWidth = signal(isPlatformServer(this.platformId) ? 0 : window.innerWidth);
  isMobile = computed(() => this.windowWidth() <= 768);
  isServer = signal(isPlatformServer(this.platformId));

  @HostListener('window:resize', ['$event']) winResize() {
    this.calcBackgroundMode();
    this.windowWidth.set(window.innerWidth);
  }
  @HostListener('window:scroll', ['$event']) docScroll() {
    this.calcBackgroundMode();
  }

  header$ = this.route.data.pipe(
    switchMap(data => {
      if (data.header) return of(data.header);
      return this.storyblokService
        .getStory('headers/website-header', this.route.snapshot)
        .pipe(map(resp => (resp && resp.story) || (false as const)));
    }),
    map(data => data?.content as { menu: MenuItem[]; logo: StoryblokImage })
  );
  private analytics = inject(PICFLOW_ANALYTICS);

  ngOnInit(): void {
    this.calcBackgroundMode();
    if (isPlatformServer(this.platformId)) return;
    this.loggedIn.set(this.browserService.getCookies()?.['picflow-loggedin'] === 'true');
  }

  private calcBackgroundMode() {
    if (isPlatformServer(this.platformId)) return;
    if (!window.document.scrollingElement) return;
    const scrollTop = window.document.scrollingElement.scrollTop;
    this.scrolled.set(scrollTop > 10);
    if (this.allowSticky() && !this.hideNav()) {
      this.headerOutOfView.set(scrollTop > 72);
    } else {
      this.headerOutOfView.set(false);
    }
  }

  openMobileMenu() {
    this.header$.pipe(take(1)).subscribe(async header => {
      const { WebsiteMobileMenuComponent } = await import(
        '../../menus/website-mobile-menu/website-mobile-menu.component'
      );
      this.dialog.open(WebsiteMobileMenuComponent, { menu: header.menu });
    });
  }

  openIntercom() {
    this.analytics.showIntercom();
  }
}
